// Client mirror of backend skillstudio grading (for live task ticking).
import { evalRef, stripAbs, fillPattern } from "@/lib/sheetEngine";
import { checkDraw } from "@/lib/drawGrade";

const DESIGN_KINDS = new Set([
  "design_named", "obj_count", "obj_count_exact", "canvas_bg", "canvas_grid", "canvas_size",
  "fill_count", "stroke_count", "stroke_width_count", "dash_count", "opacity_count", "rotated_count",
  "arrowhead_count", "text_count", "design_text_contains", "font_size_count", "font_family_count",
  "bold_count", "has_image", "has_video", "anim_count", "notes_min_words", "png_exported",
  "obj_attr", "obj_attr_min", "obj_exists", "obj_z_above", "obj_z_top", "obj_z_bottom", "obj_z_order",
  "grouped", "group_min", "ungrouped", "aligned", "spaced_evenly", "near", "far_apart", "repeated",
  "contrast_min", "balanced", "size_count",
  "section_count", "section_absent", "section_order", "section_first", "section_last",
  "section_field", "cards_filled", "nav_min", "theme_attr", "theme_contrast", "template_used",
  "template_not_blank", "alt_all", "web_optimized", "license_all_set", "no_arr_license",
  "credit_all", "previewed_mobile", "ai_used", "ai_applied", "site_published",
]);

const getBlock = (doc, bid) => (doc.blocks || []).find((b) => b.id === bid);
const firstTable = (doc) => (doc.blocks || []).find((b) => b.type === "table");
const LIST_TYPES = ["paragraph", "bullet", "number", "heading"];

export function checkTask(check, doc) {
  const k = check.kind;
  if (DESIGN_KINDS.has(k)) {
    try { return !!checkDraw(check, doc || {}); } catch (e) { return false; }
  }
  try {
    // ---- block-task "apply a skill at least N times" counters (mirror of backend _check_one) ----
    if (k === "fmt_count") {
      const blocks = doc.blocks || [];
      const attrs = check.attrs;
      const values = check.values;
      const hasNe = Object.prototype.hasOwnProperty.call(check, "not_equals");
      let n = 0;
      for (const b of blocks) {
        const fmt = b.fmt || {};
        if (attrs) { if (attrs.some((a) => fmt[a] === (check.equals !== undefined ? check.equals : true))) n += 1; }
        else if (values != null) { if (values.includes(fmt[check.attr])) n += 1; }
        else if (hasNe) { if (fmt[check.attr] !== check.not_equals) n += 1; }
        else if (fmt[check.attr] === check.equals) n += 1;
      }
      return n >= check.min;
    }
    if (k === "type_count") {
      const types = check.types || [check.equals];
      const n = (doc.blocks || []).filter((b) => types.includes(b.type)).length;
      return n >= check.min;
    }
    if (k === "fmt") { const b = getBlock(doc, check.block); return !!b && b.fmt?.[check.attr] === check.equals; }
    if (k === "fmt_all") { const bs = (doc.blocks || []).filter((b) => LIST_TYPES.includes(b.type)); return bs.length > 0 && bs.every((b) => b.fmt?.[check.attr] === check.equals); }
    if (k === "fmt_multi") { return check.blocks.every((id) => getBlock(doc, id)?.fmt?.[check.attr] === check.equals); }
    if (k === "fmt_and") { const b = getBlock(doc, check.block); return !!b && check.checks.every(([a, v]) => b.fmt?.[a] === v); }
    if (k === "type") { const b = getBlock(doc, check.block); return !!b && b.type === check.equals; }
    if (k === "type_multi") { return check.blocks.every((id) => getBlock(doc, id)?.type === check.equals); }
    if (k === "text_contains") { const b = getBlock(doc, check.block); return !!b && (b.text || "").includes(check.value); }
    if (k === "text_replaced") { const b = getBlock(doc, check.block); if (!b) return false; const t = (b.text || "").toLowerCase(); return !t.includes(check.remove.toLowerCase()) && t.includes(check.add.toLowerCase()); }
    if (k === "link") { const b = getBlock(doc, check.block); return (b?.fmt?.link || "").startsWith("http"); }
    if (k === "header_contains") { return (doc.header || "").toLowerCase().includes(check.value.toLowerCase()); }
    if (k === "footer_pagenum") { return !!doc.footerPageNumber; }
    if (k === "table") { const t = firstTable(doc); return !!t && t.cols === check.cols && t.rows === check.rows; }
    if (k === "table_cell_filled") { const t = firstTable(doc); if (!t) return false; const cells = t.cells || []; const { row, col } = check; return row < cells.length && col < cells[row].length && !!(cells[row][col] || "").trim(); }
    if (k === "table_cells_filled") { const t = firstTable(doc); if (!t) return false; const cells = t.cells || []; const n = cells.reduce((a, r) => a + r.filter((c) => (c || "").trim()).length, 0); return n >= check.min; }
    if (k === "exported") { return !!doc.exported; }
    // ---- document-editor kinds (blocks 4-5) ----
    const styleOf = (b) => (b && b.style) || "normal";
    const images = () => (doc.blocks || []).filter((b) => b.type === "image");
    if (k === "word_fmt") {
      const b = getBlock(doc, check.block); if (!b) return false;
      if (b.fmt?.[check.attr] === check.equals) return true;
      const w = check.word.toLowerCase();
      return (b.spans || []).some((sp) => (sp.text || "").toLowerCase().includes(w) && sp[check.attr] === check.equals);
    }
    if (k === "page") return (doc.page || {})[check.attr] === check.equals;
    if (k === "style") { const b = getBlock(doc, check.block); return !!b && styleOf(b) === check.equals; }
    if (k === "style_multi") return check.blocks.every((id) => styleOf(getBlock(doc, id)) === check.equals);
    if (k === "style_count") return (doc.blocks || []).filter((b) => check.styles.includes(styleOf(b))).length >= check.min;
    if (k === "style_and_fmt") { const b = getBlock(doc, check.block); return !!b && styleOf(b) === check.style && b.fmt?.[check.attr] === check.equals; }
    if (k === "pagebreak") { const b = getBlock(doc, check.block); return !!b && !!b.pageBreakBefore; }
    if (k === "pagebreak_count") return (doc.blocks || []).filter((b) => b.pageBreakBefore).length >= check.min;
    if (k === "toc") return (doc.blocks || []).some((b) => b.type === "toc");
    if (k === "ruler_shown") return !!doc.showRuler;
    if (k === "outline_shown") return !!doc.showOutline;
    if (k === "image") {
      const imgs = images(); if (!imgs.length) return false;
      if ("src" in check) return imgs.some((i) => i.src === check.src);
      if ("size" in check) return imgs.some((i) => i.size === check.size);
      if ("wrap" in check) return imgs.some((i) => i.wrap === check.wrap);
      if (check.caption) return imgs.some((i) => (i.caption || "").trim());
      return true;
    }
    if (k === "image_count") {
      let imgs = images();
      if (check.wrapped) imgs = imgs.filter((i) => i.wrap === "wrap" || i.wrap === "break");
      if (check.resized) imgs = imgs.filter((i) => i.size === "small" || i.size === "large");
      if (check.captioned) imgs = imgs.filter((i) => (i.caption || "").trim());
      return imgs.length >= check.min;
    }
    if (k === "shared_with") return (doc.shared || []).some((p) => (!("email" in check) || (p.email || "").toLowerCase() === check.email.toLowerCase()) && (!("role" in check) || p.role === check.role));
    if (k === "shared_count") return (doc.shared || []).length >= check.min;
    if (k === "comment_on") return (doc.comments || []).some((c) => c.blockId === check.block && (c.text || "").trim());
    if (k === "comment_count") return (doc.comments || []).filter((c) => (c.text || "").trim()).length >= check.min;
    if (k === "mode") return (doc.mode || "editing") === check.equals;
    if (k === "version_named") return !!(doc.versionName || "").trim();
    if (k === "word_count_opened") return !!doc.wordCountOpened;
    if (k === "defined") return (doc.definedWord || "").trim().toLowerCase() === check.word.toLowerCase();
    if (k === "voice_typed") return !!doc.voiceTyped;
    if (k === "title_contains") return (doc.title || "").toLowerCase().includes(check.value.toLowerCase());
    if (k === "folder") return (doc.folder || "") === check.equals;
    if (k === "title_and_folder") return (doc.title || "").toLowerCase().includes(check.value.toLowerCase()) && (doc.folder || "") === check.folder;
    if (k === "downloaded") return (doc.downloaded || []).includes(check.format);
    if (k === "printed") return !!doc.printed;
    // ---- sheets kinds ----
    if (["cell_text", "cell_formula", "cell_value", "sorted", "cell_format", "cell_style", "merged",
         "frozen", "col_width", "grid_size", "filtered", "formula_absolute", "formula_fill", "cond_fmt",
         "rows_exact", "col_contains"].includes(k)) {
      const sheets = doc.sheets || [];
      const si = check.sheet || 0;
      if (si >= sheets.length) return false;
      const sh = sheets[si];
      const cells = sh.cells || {};
      const fmts = sh.fmt || {};
      const norm = (x) => stripAbs(String(x == null ? "" : x)).replace(/\s/g, "").toUpperCase();
      if (k === "cell_text") {
        const raw = (cells[check.cell] || "").trim(); const want = check.equals.toLowerCase();
        if (raw.startsWith("=")) return String(evalRef(cells, check.cell) ?? "").trim().toLowerCase() === want;
        return raw.toLowerCase() === want;
      }
      if (k === "cell_formula") return norm(cells[check.cell]) === norm(`=${check.fn}(${check.range})`);
      if (k === "formula_absolute") {
        const raw = (cells[check.cell] || "").trim();
        if (!raw.includes("$") || !raw.startsWith("=")) return false;
        if (check.anchor && !raw.replace(/\s/g, "").toUpperCase().includes(check.anchor.replace(/\s/g, "").toUpperCase())) return false;
        return check.equals ? norm(raw) === norm(check.equals) : true;
      }
      if (k === "formula_fill") {
        const col = check.col.toUpperCase();
        for (let r = check.from; r <= check.to; r++) if (norm(cells[`${col}${r}`]) !== norm(fillPattern(check.pattern, r))) return false;
        return true;
      }
      if (k === "cell_format") return (fmts[check.cell] || {}).numFmt === check.equals;
      if (k === "cell_style") { const f = fmts[check.cell] || {}; return "equals" in check ? f[check.feature] === check.equals : !!f[check.feature]; }
      if (k === "merged") { const want = norm(check.range); return (sh.merges || []).some((m) => norm(m) === want); }
      if (k === "frozen") return Number(sh.freeze || 0) >= (check.rows || 1);
      if (k === "col_width") return Number((sh.colWidths || {})[check.col.toUpperCase()] || 0) >= check.min;
      if (k === "grid_size") return (!("rows" in check) || Number(sh.rows || 0) === check.rows) && (!("cols" in check) || Number(sh.cols || 0) === check.cols);
      if (k === "filtered") {
        const flt = sh.filter || {};
        if (!flt.range) return false;
        if (check.range && norm(flt.range) !== norm(check.range)) return false;
        if (check.col && (flt.col || "").toUpperCase() !== check.col.toUpperCase()) return false;
        if ("equals" in check) return String(flt.value || "").trim().toLowerCase() === String(check.equals).trim().toLowerCase();
        return true;
      }
      if (k === "cond_fmt") {
        const want = norm(check.range);
        return (sh.condFmt || []).some((rule) => norm(rule.range) === want
          && (!check.op || rule.op === check.op)
          && (!("value" in check) || Number(rule.value) === Number(check.value)));
      }
      if (k === "cell_value") { const v = evalRef(cells, check.cell); return typeof v === "number" && Math.abs(v - check.equals) < 0.001; }
      if (k === "sorted") {
        const col = check.col.toUpperCase(); const nums = [];
        for (let r = check.from; r <= check.to; r++) { const n = Number((cells[`${col}${r}`] || "").trim()); if (!Number.isNaN(n) && (cells[`${col}${r}`] || "").trim() !== "") nums.push(n); }
        if (nums.length < 2) return false;
        if (check.order === "asc") return nums.every((n, i) => i === 0 || nums[i - 1] <= n);
        return nums.every((n, i) => i === 0 || nums[i - 1] >= n);
      }
      if (k === "col_contains") {
        const col = check.col.toUpperCase(), want = String(check.equals).trim().toLowerCase();
        for (let r = check.from; r <= check.to; r++) if ((cells[`${col}${r}`] || "").trim().toLowerCase() === want) return true;
        return false;
      }
      if (k === "rows_exact") {
        const same = (a, b) => {
          const na = Number(String(a).trim()), nb = Number(String(b).trim());
          if (String(a).trim() !== "" && String(b).trim() !== "" && !Number.isNaN(na) && !Number.isNaN(nb)) return Math.abs(na - nb) < 0.001;
          return String(a).trim().toLowerCase() === String(b).trim().toLowerCase();
        };
        const cols = (check.cols || []).map((c) => c.toUpperCase());
        const want = check.rows || [];
        if (want.length !== check.to - check.from + 1) return false;
        return want.every((row, i) => cols.every((col, ci) => same(cells[`${col}${check.from + i}`] || "", row[ci])));
      }
    }
    if (k === "chart_titled") return (doc.charts || []).some((c) => (!check.type || c.type === check.type)
      && !!(c.title || "").trim() && (!check.contains || (c.title || "").toLowerCase().includes(check.contains.toLowerCase())));
    if (k === "chart_axes") return (doc.charts || []).some((c) => {
      if (check.type && c.type !== check.type) return false;
      const x = (c.xTitle || "").trim(), y = (c.yTitle || "").trim();
      if (check.x !== false && !x) return false;
      if (check.y !== false && !y) return false;
      if (check.x_contains && !x.toLowerCase().includes(check.x_contains.toLowerCase())) return false;
      if (check.y_contains && !y.toLowerCase().includes(check.y_contains.toLowerCase())) return false;
      return true;
    });
    if (k === "chart_legend") return (doc.charts || []).some((c) => (!check.type || c.type === check.type)
      && !!c.legend === (check.equals !== false));
    if (k === "chart") return (doc.charts || []).some((c) => c.type === check.type);
    if (k === "chart_range") { const want = check.range.replace(/\s/g, "").toUpperCase(); return (doc.charts || []).some((c) => c.type === check.type && (c.range || "").replace(/\s/g, "").toUpperCase() === want); }
    if (k === "sheet_count") return (doc.sheets || []).length === check.equals;
    if (k === "sheet_named") { const s = doc.sheets || []; return check.index < s.length && (s[check.index].name || "").trim().toLowerCase() === check.name.toLowerCase(); }
    // ---- slides kinds ----
    if (k === "slide_count") return (doc.slides || []).length === check.equals;
    if (k === "deck_named") { const name = (doc.name || "").trim(); if (!name || name.toLowerCase() === "untitled presentation") return false; return check.contains ? name.toLowerCase().includes(check.contains.toLowerCase()) : true; }
    if (k === "slide_order_titles") {
      const slides = doc.slides || []; const want = check.titles || [];
      if (slides.length < want.length) return false;
      return want.every((w, i) => { const t = (slides[i].title || "").trim().toLowerCase(); if (w === "*") return !!t; return !w || t.includes(w.toLowerCase()); });
    }
    if (k === "presented") return !!doc.presented;
    if (k === "slides_all_transition") { const sl = doc.slides || []; return sl.length > 0 && sl.every((s) => (s.transition || "none") === check.equals); }
    if (k === "slide_title_absent") return !(doc.slides || []).some((s) => (s.title || "").toLowerCase().includes(check.value.toLowerCase()));
    if (k === "slides_identical") {
      const slides = doc.slides || []; const { a, b } = check;
      if (a === b || a >= slides.length || b >= slides.length) return false;
      const key = (s) => JSON.stringify([(s.title || "").trim().toLowerCase(), (s.bullets || []).map((x) => x.trim()).filter(Boolean), s.layout, s.theme]);
      return !!(slides[a].title || "").trim() && key(slides[a]) === key(slides[b]);
    }
    if (k.startsWith("slide_") || k === "five_by_five") {
      const slides = doc.slides || [];
      const si = check.slide || 0;
      if (si >= slides.length) return false;
      const sl = slides[si];
      const bullets = (sl.bullets || []).filter((b) => (b || "").trim());
      if (k === "slide_title_nonempty") return !!(sl.title || "").trim();
      if (k === "slide_title_contains") return (sl.title || "").toLowerCase().includes(check.value.toLowerCase());
      if (k === "slide_bullets_min") return bullets.length >= check.min;
      if (k === "five_by_five") return bullets.length >= 1 && bullets.length <= 5 && bullets.every((b) => b.trim().split(/\s+/).length <= 5);
      if (k === "slide_layout") return sl.layout === check.equals;
      if (k === "slide_theme") return sl.theme === check.equals;
      if (k === "slide_has_image") return !!sl.image;
      if (k === "slide_has_chart") return !!sl.chart && (!("type" in check) || sl.chart.type === check.type);
      if (k === "slide_animation") { const a = sl.animation || "none"; return check.equals ? a === check.equals : a !== "none"; }
      if (k === "slide_transition") { const t = sl.transition || "none"; return check.equals ? t === check.equals : t !== "none"; }
      if (k === "slide_notes_min_words") return (sl.notes || "").trim().split(/\s+/).filter(Boolean).length >= check.min;
      if (k === "slide_bullet_style") return bullets.length >= 1 && (sl.bulletStyle || "dot") === check.equals;
      if (k === "slide_body_min_words") return (sl.body || "").trim().split(/\s+/).filter(Boolean).length >= check.min;
      if (k === "slide_background") return !!sl.background && (!("equals" in check) || sl.background === check.equals);
      if (k === "slide_has_table") return !!sl.table && (!("rows" in check) || Number(sl.table.rows) === check.rows) && (!("cols" in check) || Number(sl.table.cols) === check.cols);
      if (k === "slide_has_video") return !!sl.video && (!("equals" in check) || sl.video.id === check.equals);
      if (k === "slide_image_is") return !!sl.image && sl.image.id === check.equals;
    }
    // ---- email kinds ----
    if (k === "picked") return (doc.picked || []).includes(check.field);
    if (k === "opened_count") return (doc.messages || []).filter((m) => m.folder === "inbox" && m.read).length >= check.min;
    if (k === "sent_count") { const sk = check.sentKind; return (doc.messages || []).filter((m) => m.folder === "sent" && (sk == null || m.kind === sk)).length >= check.min; }
    if (k === "email_opened") return (doc.messages || []).some((m) => m.id === check.id && m.read);
    if (k === "searched") return !!doc.searched;
    if (["sent_exists", "subject_prefix", "subject_nonempty", "subject_and_to", "to_includes", "cc_includes", "bcc_includes", "cc_min", "has_greeting", "has_signoff", "has_greeting_signoff", "has_attachment", "body_min_words", "formatting"].includes(k)) {
      const sent = (doc.messages || []).filter((m) => m.folder === "sent" && m.kind === check.sentKind);
      const m = sent[sent.length - 1];
      if (k === "sent_exists") return !!m;
      if (!m) return false;
      const body = m.body || "", bl = body.toLowerCase(), subj = (m.subject || "").trim();
      const sbody = (m.bodyStudent != null ? m.bodyStudent : body), sbl = sbody.toLowerCase();
      const inList = (arr, e) => (arr || []).map((x) => x.toLowerCase()).includes(e.toLowerCase());
      const GRE = ["dear ", "hi ", "hi,", "hello", "good morning", "good afternoon", "hey ", "greetings"];
      const SIG = ["thanks", "thank you", "sincerely", "best,", "best regards", "regards", "cheers", "respectfully", "yours"];
      if (k === "subject_prefix") return subj.toLowerCase().startsWith(check.prefix.toLowerCase());
      if (k === "subject_nonempty") return !!subj;
      if (k === "subject_and_to") return !!subj && inList(m.to, check.email);
      if (k === "to_includes") return inList(m.to, check.email);
      if (k === "cc_includes") return inList(m.cc, check.email);
      if (k === "bcc_includes") return inList(m.bcc, check.email);
      if (k === "cc_min") return (m.cc || []).length >= check.min;
      if (k === "has_greeting") return GRE.some((g) => sbl.includes(g));
      if (k === "has_signoff") return SIG.some((s) => sbl.includes(s));
      if (k === "has_greeting_signoff") return GRE.some((g) => sbl.includes(g)) && SIG.some((s) => sbl.includes(s));
      if (k === "has_attachment") return (m.attachments || []).length > 0 && (!check.name || (m.attachments || []).some((a) => a.name === check.name));
      if (k === "body_min_words") return sbody.split(/\s+/).filter(Boolean).length >= check.min;
      if (k === "formatting") return { bold: m.hasBold, bullets: m.hasBullets, signature: m.hasSignature }[check.feature] || false;
    }
  } catch (e) { return false; }
  return false;
}
